import React, { useState, useEffect } from 'react';
import { X, Calendar, Building2, User, Tag, Shield, FileText, Download } from 'lucide-react';
import { records as recordsApi } from '../utils/api';

/** Purpose: Executes { record, on close }. Inputs: { record, onClose }. Output: React render output (JSX). */
export default function RecordDetail({ record, onClose }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');
  const [previewFileType, setPreviewFileType] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    let objectUrl = '';
    let active = true;

    /** Purpose: Loads preview document for this record. Inputs: none. Output: Promise<void>. */
    const loadPreview = async () => {
        setLoadingPreview(true);
        setPreviewError('');

      try {
        const response = await recordsApi.download(record.id, { intent: 'preview' });
        if (!active) return;

        objectUrl = window.URL.createObjectURL(response.blob);
        setPreviewUrl(objectUrl);
        setPreviewFileName(response.fileName);
        setPreviewFileType(response.fileType);
      } catch (error) {
        if (!active) return;
        setPreviewError(error.message || 'Failed to load record preview');
      } finally {
        if (active) setLoadingPreview(false);
      }
    };

    loadPreview();

    return () => {
      active = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [record.id]);

  /** Purpose: Executes format date. Inputs: dateStr. Output: localized date string. */
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  /** Purpose: Checks if a file can render inline preview. Inputs: fileType. Output: boolean. */
  const isInlinePreviewable = (fileType) => {
    return fileType && (fileType.startsWith('image/') || fileType === 'application/pdf' || fileType.startsWith('text/'));
  };

  /** Purpose: Downloads the loaded record file. Inputs: none. Output: void. */
  const handleDownload = async () => {
    try {
      const response = await recordsApi.download(record.id, { intent: 'download' });
      const downloadUrl = window.URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = response.fileName || `${record.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setPreviewError(err.message || 'Failed to download file');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-white" />
            <h2 className="text-xl font-semibold text-white">Record Details</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="px-3 py-2 bg-white/15 hover:bg-white/25 rounded-lg transition-colors text-white text-sm flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] space-y-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-2xl font-bold text-slate-900">{record.title}</h3>
              <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                {record.type}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 text-slate-600">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Record Date</p>
                  <p className="font-medium">{formatDate(record.date)}</p>
                </div>
              </div>

              {record.provider && (
                <div className="flex items-center gap-3 text-slate-600">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Provider</p>
                    <p className="font-medium">{record.provider}</p>
                  </div>
                </div>
              )}

              {record.facility && (
                <div className="flex items-center gap-3 text-slate-600">
                  <Building2 className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Facility</p>
                    <p className="font-medium">{record.facility}</p>
                  </div>
                </div>
              )}

              {record.encrypted && (
                <div className="flex items-center gap-3 text-green-600">
                  <Shield className="w-5 h-5" />
                  <div>
                    <p className="text-xs text-green-500">Security</p>
                    <p className="font-medium">Encrypted</p>
                  </div>
                </div>
              )}
            </div>

            {record.description && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500 mb-1">Description</p>
                <p className="text-slate-700">{record.description}</p>
              </div>
            )}

            {record.tags && record.tags.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-4 h-4 text-slate-400" />
                {record.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h4 className="text-lg font-semibold text-slate-900 mb-4">Document Preview</h4>
            {loadingPreview && (
              <div className="text-center py-10 text-slate-500">Loading document preview...</div>
            )}
            {!loadingPreview && previewError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {previewError}
              </div>
            )}
            {!loadingPreview && !previewError && !previewUrl && (
              <div className="text-sm text-slate-600">No document is attached to this record.</div>
            )}
            {!loadingPreview && !previewError && previewUrl && isInlinePreviewable(previewFileType) && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                {previewFileType.startsWith('image/') ? (
                  <img src={previewUrl} alt={previewFileName || record.title} className="max-h-[520px] w-auto mx-auto" />
                ) : (
                  <iframe
                    src={previewUrl}
                    title={previewFileName || record.title}
                    className="w-full h-[520px] bg-white"
                  />
                )}
              </div>
            )}
            {!loadingPreview && !previewError && previewUrl && !isInlinePreviewable(previewFileType) && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-700">
                This file type cannot be previewed inline. Use download to open it locally.
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
